import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../api';
import PVMSDashboard from '../components/payment-voucher/PVMSDashboard';
import LoadApprovedRequisitionTable from '../components/payment-voucher/LoadApprovedRequisitionTable';
import PaymentVoucherFormRoute from '../components/payment-voucher/PaymentVoucherFormRoute';
import MyVouchers from '../components/payment-voucher/MyVouchers';
import VoucherApprovalQueue from '../components/payment-voucher/VoucherApprovalQueue';
import VoucherReviewRelease from '../components/payment-voucher/VoucherReviewRelease';
import PostedVoucherRegister from '../components/payment-voucher/PostedVoucherRegister';
import VoucherMethodModal from '../components/payment-voucher/VoucherMethodModal';

export default function PaymentVoucherEnginePage({ toast }) {
  const navigate = useNavigate();
  const params = useParams();

  // The tab IS the url, same as the requisition module. No local state to fall
  // out of step with the address bar.
  const activeTab = params.tab || 'dashboard';
  // Everything after it, for the views nested inside a tab.
  const subPath = (params['*'] || '').split('/').filter(Boolean);
  // Every screen in this module is now an address. What used to be overlay
  // state — the load table and the voucher form — reads from the url, and
  // PaymentVoucherFormRoute turns the ids back into the objects the form
  // expects before mounting it.
  const [currentUser, setCurrentUser] = useState(null);
  // "Create Payment Voucher" opens this first — the chosen method decides the
  // form's header title and is saved on the voucher.
  const [showMethodModal, setShowMethodModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.getMe();
        setCurrentUser(me);
      } catch (e) {
        console.error('Failed to fetch user', e);
      }
    })();
  }, []);

  const userRoleNames = (currentUser?.roles || []).map(r =>
    typeof r === 'string' ? r.toLowerCase() : (r.role_name || r.name || '').toLowerCase()
  );
  // Only finance_head can actually act on the Finance Head queue — deliberately
  // excludes super_admin, per explicit request.
  const canSeeFinanceHeadQueue = userRoleNames.some(r => r === 'finance_head');
  // "Approval Queue" tab: md/dmd, plus finance_head so they can follow a
  // voucher past their own approval. Still deliberately excludes super_admin,
  // per explicit request.
  //
  // Visibility only. Who may ACT on that screen is read from the roles
  // configured on the final PAYMENT_VOUCHER workflow step — currently md and
  // dmd — so finance_head opens it read-only and the screen says so. Widening
  // this gate cannot widen the approval itself.
  const canSeeApprovalQueue = userRoleNames.some(
    r => r === 'md' || r === 'dmd' || r === 'finance_head',
  );
  // "Approve Posted Vouchers" TAB: finance_head, md, dmd only — explicitly NOT
  // super_admin, and NOT the plain payment_voucher role (which only raises/
  // views its own vouchers). This is separate from the Posted Vouchers
  // Register (Workspace 3 on the dashboard), which is a different feature
  // and a different data set — see PostedVoucherRegister.js.
  const canSeeFinanceHeadQueueTab = userRoleNames.some(r => r === 'finance_head' || r === 'md' || r === 'dmd');

  const navItems = [
    { id: 'dashboard', label: 'PVMS Dashboard' },
    { id: 'my-vouchers', label: 'My Vouchers' },
  ];
  if (canSeeFinanceHeadQueueTab) navItems.push({ id: 'finance-head-queue', label: 'Approve Posted Vouchers' });
  if (canSeeApprovalQueue) navItems.push({ id: 'approval-queue', label: 'Approval Queue' });
  // Last, after the two approval queues. A company-wide read-only record,
  // open to any signed-in user — same access it had as a dashboard card —
  // so it is pushed unconditionally rather than behind a role check.
  navItems.push({ id: 'posted-register', label: 'Voucher Register', accent: '#0f766e' });

  const closeOverlay = () => selectTab('my-vouchers');

  // The method picker is a modal, not a screen: it must not survive a move to
  // a different address.
  useEffect(() => { setShowMethodModal(false); }, [activeTab]);
  // Saving lands the reader on their vouchers, which is where the new one is.
  const onSavedInOverlay = () => selectTab('my-vouchers');
  // A voucher id looks like a uuid; the slot is shared with nothing else here,
  // but it is checked rather than assumed so a stray segment opens nothing
  // instead of a broken detail view.
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const openVoucherId = subPath.find((seg) => UUID.test(seg)) || null;
  const voucherNav = (tab) => (id) => navigate(
    id ? `/payment-voucher-engine/${tab}/${id}` : `/payment-voucher-engine/${tab}`,
  );

  // Which of the three ways into the form this address is, if any.
  //   /new                              a voucher with no method chosen yet
  //   /new/<methodId>                   ... with one
  //   /new/from-requisition/<prId>      loaded from an approved requisition
  //   /my-vouchers/<voucherId>/edit     reopening a draft
  const formRoute = (() => {
    if (activeTab === 'new') {
      if (subPath[0] === 'from-requisition' && subPath[1]) return { requisitionId: subPath[1] };
      return { methodId: subPath[0] || null };
    }
    if (activeTab === 'my-vouchers' && openVoucherId && subPath.includes('edit')) {
      return { voucherId: openVoucherId };
    }
    return null;
  })();

  const selectTab = (id) => {
    api.clearApiCache();
    setShowMethodModal(false);
    navigate(`/payment-voucher-engine/${id}`);
  };

  return (
    <div className="fade-in" style={{ padding: '0px 0px 24px', marginTop: -28 }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, paddingTop: 16, paddingBottom: 8, alignItems: 'stretch' }}>
        {navItems.map(item => {
          const isActive = !formRoute && activeTab === item.id;
          // The Voucher Register is a read-only archive rather than a place
          // work gets done, so it is tinted teal to set it apart at rest.
          // Selected, it takes the same navy as every other tab — the active
          // state has to mean one consistent thing across the bar.
          const accent = item.accent;
          return (
          <button
            key={item.id}
            onClick={() => selectTab(item.id)}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: isActive ? '#0b318bff' : (accent ? `${accent}14` : 'var(--bg-card)'),
              color: isActive ? '#ffffff' : (accent || 'var(--text-primary)'),
              border: accent && !isActive ? `1px solid ${accent}` : '1px solid black',
              borderRadius: 12,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              textAlign: 'center',
              boxShadow: isActive ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
              whiteSpace: 'normal',
              lineHeight: 1.25,
            }}
          >
            {item.label}
          </button>
          );
        })}
      </div>

      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        {activeTab === 'load-requisition' && (
          <LoadApprovedRequisitionTable
            toast={toast}
            onBack={() => selectTab('dashboard')}
            onLoad={(detail) => navigate(
              `/payment-voucher-engine/new/from-requisition/${detail?.purchase_request_id || detail?.id}`)}
          />
        )}
        {/* /new/<methodId>, /new/from-requisition/<prId>, /my-vouchers/<id>/edit */}
        {formRoute && (
          <PaymentVoucherFormRoute
            toast={toast}
            voucherId={formRoute.voucherId}
            requisitionId={formRoute.requisitionId}
            methodId={formRoute.methodId}
            onBack={formRoute.requisitionId
              ? () => navigate('/payment-voucher-engine/load-requisition')
              : closeOverlay}
            onSaved={onSavedInOverlay}
          />
        )}
        {!formRoute && activeTab === 'dashboard' && (
          <PVMSDashboard
            toast={toast}
            onOpenLoadRequisition={() => navigate('/payment-voucher-engine/load-requisition')}
            onOpenNewVoucher={() => setShowMethodModal(true)}
            onOpenApproveVouchers={() => selectTab('finance-head-queue')}
            onOpenApprovalQueue={() => selectTab('approval-queue')}
            canApproveVouchers={canSeeFinanceHeadQueueTab}
            canSeeApprovalQueue={canSeeApprovalQueue}
          />
        )}
        {!formRoute && activeTab === 'posted-register' && (
          <PostedVoucherRegister
            toast={toast}
            openVoucherId={openVoucherId}
            onOpenVoucher={voucherNav('posted-register')}
          />
        )}
        {!formRoute && activeTab === 'my-vouchers' && (
          <MyVouchers
            toast={toast}
            openVoucherId={formRoute ? null : openVoucherId}
            onOpenVoucher={voucherNav('my-vouchers')}
            onOpenNewVoucher={() => setShowMethodModal(true)}
            onEditDraft={(voucher) => navigate(
              `/payment-voucher-engine/my-vouchers/${voucher.payment_voucher_id}/edit`)}
          />
        )}
        {!formRoute && activeTab === 'finance-head-queue' && canSeeFinanceHeadQueueTab && (
          <VoucherApprovalQueue
            toast={toast}
            openVoucherId={openVoucherId}
            onOpenVoucher={voucherNav('finance-head-queue')}
            canApprove={canSeeFinanceHeadQueue}
            currentUser={currentUser}
            title="Approve Posted Vouchers"
            description="All payment vouchers raised across the company, awaiting Finance Head approval before they move to MD/DMD for final authorisation."
            fetchFn={() => api.getFinanceHeadVoucherQueue()}
          />
        )}
        {/* The MD/DMD Approval Queue was replaced by Voucher Review & Release:
            same workflow step, but the full review screen with the release
            gate into the Expense Register. Who may act is read from that
            step's configured roles, not from this component. */}
        {!formRoute && activeTab === 'approval-queue' && canSeeApprovalQueue && (
          <VoucherReviewRelease toast={toast} currentUser={currentUser} />
        )}
      </div>

      {showMethodModal && (
        <VoucherMethodModal
          toast={toast}
          onClose={() => setShowMethodModal(false)}
          onSelectMethod={(method) => {
            setShowMethodModal(false);
            navigate(`/payment-voucher-engine/new/${method.id}`);
          }}
          onLoadRequisition={() => {
            setShowMethodModal(false);
            navigate('/payment-voucher-engine/load-requisition');
          }}
        />
      )}
    </div>
  );
}
